import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Radio, Search, Calendar, Loader2, AlertTriangle, CheckCircle2, Printer, Send,
  FileText, Save, ExternalLink, RefreshCcw, Info, ArrowLeft, X, Copy, Mail, MessageCircle,
  ClipboardList, MoreHorizontal, ChevronDown, Sparkles, ShieldCheck,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { TopBar } from "@/components/PageShell";
import { usePaywall } from "@/hooks/usePaywall";
import { CourtLabel } from "@/components/CourtLabel";
import { pickDisplayText, sanitizeDjenText } from "@/lib/djenText";

const fmtDate = (s) => s ? new Date(s + "T00:00:00").toLocaleDateString("pt-BR") : "—";
const fmtDateTime = (s) => s ? new Date(s).toLocaleString("pt-BR", {day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "—";
const todayIso = () => new Date().toISOString().slice(0,10);
const addDaysIso = (n) => new Date(Date.now() + n*86400000).toISOString().slice(0,10);

function ProgressStages({ stages, active }) {
  return (
    <div className="space-y-2">
      {stages.map((s, i) => {
        const done = i < active;
        const running = i === active;
        return (
          <div key={i} className="flex items-center gap-3 text-sm">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${done ? "bg-emerald-500" : running ? "bg-rose-500 animate-pulse" : "bg-slate-200"}`}>
              {done && <CheckCircle2 className="w-3 h-3 text-white"/>}
            </div>
            <span className={done ? "text-slate-500 line-through" : running ? "text-slate-900 font-medium" : "text-slate-400"}>{s}</span>
          </div>
        );
      })}
    </div>
  );
}

function IncompleteOAB({ nome, onFix }) {
  return (
    <div className="flex-1 min-w-0 flex flex-col p-6 lg:p-8 font-sans max-w-2xl mx-auto w-full" data-testid="djen-incomplete">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0"/>
          <div>
            <h2 className="font-heading text-xl font-semibold text-slate-900">Cadastro incompleto</h2>
            <p className="text-sm text-slate-700 mt-1">
              Para consultar o DJEN é necessário cadastrar o número e a UF da sua inscrição na OAB.
              {nome && <> Advogado: <strong>{nome}</strong>.</>}
            </p>
            <button onClick={onFix} data-testid="djen-fix-oab-btn"
              className="mt-4 h-10 px-4 rounded-lg bg-[#d4a24c] hover:bg-[#c48a3e] text-black font-semibold text-sm">
              Completar cadastro
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const PERIODS = [
  { key: "today", label: "Hoje" },
  { key: "yesterday", label: "Ontem" },
  { key: "last3", label: "Últimos 3 dias" },
  { key: "last7", label: "Últimos 7 dias" },
  { key: "last15", label: "Últimos 15 dias" },
  { key: "last30", label: "Últimos 30 dias" },
];

export default function Djen() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [consulting, setConsulting] = useState(false);
  const [stage, setStage] = useState(0);
  const [results, setResults] = useState({ rows: [], total: 0 });
  const [lastResult, setLastResult] = useState(null);
  const [showPeriod, setShowPeriod] = useState(false);
  const [customRange, setCustomRange] = useState({ start: addDaysIso(-7), end: todayIso() });
  const [reader, setReader] = useState(null); // open card modal
  const [sender, setSender] = useState(null); // sender modal { pub, channel }
  const [analyzingId, setAnalyzingId] = useState(null);
  const [livePop, setLivePop] = useState(null);   // { count, publications } animated pop
  const [fulfill, setFulfill] = useState(null);   // publication being fulfilled via drawer
  const { authorize, PaywallElement } = usePaywall("IA_OPERATION");

  const analyze = async (pub) => {
    // Análise IA já cached? backend detecta e não recobra, mas o frontend
    // deve gatear apenas quando é chamada nova (sem ai_analysis prévia).
    if (pub.ai_analysis) return;
    const authorization = await authorize({ quantity: 1 });
    if (!authorization) { toast.info("Análise cancelada."); return; }
    setAnalyzingId(pub.id);
    try {
      const r = await api.post(`/djen/publications/${pub.id}/analyze`);
      // Se veio cached (não gerou custo real), libera a reserva
      if (r.data.cached) {
        await authorization.release();
      } else {
        await authorization.commit();
      }
      setResults(prev => ({ ...prev, rows: prev.rows.map(x => x.id === pub.id ? { ...x, ai_analysis: r.data.analysis, linked_client: r.data.linked_client } : x)}));
      toast.success("Análise concluída");
    } catch (e) {
      await authorization.release();
      toast.error(e?.response?.data?.detail || "Falha na análise IA");
    } finally { setAnalyzingId(null); }
  };

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await api.get("/djen/status");
      setState(r.data);
      // Se não houver sonda recente (>5 min), dispara diagnóstico ao vivo em background
      const last = r.data?.connectivity?.last_probe?.checked_at;
      const stale = !last || (Date.now() - new Date(last).getTime() > 5 * 60 * 1000);
      if (stale && !r.data?.mock_mode) {
        api.post("/djen/diagnostic").then((d) => {
          setState((prev) => prev ? { ...prev, connectivity: {
            ...(prev.connectivity || {}),
            last_probe: d.data?.current,
            last_success_at: d.data?.last_success_at,
            last_error_at: d.data?.last_error_at,
          }} : prev);
        }).catch(() => {});
      }
    } catch (e) {
      toast.error("Não foi possível carregar o status DJEN");
    } finally { setLoading(false); }
  };

  const loadPublications = async (filters = {}) => {
    try {
      const r = await api.get("/djen/publications", { params: { page_size: 30, ...filters }});
      setResults(r.data);
    } catch {/* silent */}
  };

  useEffect(() => {
    loadStatus();
    loadPublications();
  }, []);

  // ==== Live log: polling leve (30s) do status para detectar novas publicações
  // e animar um pop no canto direito. Deduplica por unread_count. ====
  useEffect(() => {
    let prevUnread = null;
    const tick = async () => {
      if (document.hidden) return;
      try {
        const r = await api.get("/djen/status");
        const cur = r.data;
        if (prevUnread !== null && cur.unread_count > prevUnread) {
          const delta = cur.unread_count - prevUnread;
          // busca os N mais novos para preview
          try {
            const p = await api.get("/djen/publications",
              { params: { page_size: Math.min(delta, 5), unread_only: true } });
            setLivePop({ count: delta, publications: p.data.rows || [] });
          } catch { setLivePop({ count: delta, publications: [] }); }
          // atualiza lista principal se o usuário já viu a página
          loadPublications();
        }
        prevUnread = cur.unread_count;
        setState(cur);
      } catch { /* silent */ }
    };
    const id = setInterval(tick, 30000);
    // primeiro tick após 5s para estabelecer baseline
    const boot = setTimeout(tick, 5000);
    return () => { clearInterval(id); clearTimeout(boot); };
  }, []);

  // ==== Fallback anti-geobloqueio: fetch direto do navegador (que está no BR) ====
  // O backend está fora do Brasil e recebe HTTP 403 do CloudFront do CNJ.
  // Como o browser do advogado está no Brasil, ele consegue chamar a API oficial
  // diretamente e, em seguida, enviar o JSON cru para o backend gravar (com dedup).
  const rangeFromMode = (mode, extra) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0,10);
    const minus = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
    switch (mode) {
      case "today":     return { start: iso(today), end: iso(today) };
      case "yesterday": { const d = minus(1); return { start: iso(d), end: iso(d) }; }
      case "last3":  return { start: iso(minus(2)),  end: iso(today) };
      case "last7":  return { start: iso(minus(6)),  end: iso(today) };
      case "last15": return { start: iso(minus(14)), end: iso(today) };
      case "last30": return { start: iso(minus(29)), end: iso(today) };
      case "period": return { start: extra.start, end: extra.end };
      default: return { start: iso(today), end: iso(today) };
    }
  };

  const browserFetchDjen = async (mode, extra = {}) => {
    const { start, end } = rangeFromMode(mode, extra);
    if (!start || !end) throw new Error("range inválido");
    const numeroOab = String(state.oab_numero || "").replace(/[^0-9]/g, "");
    const ufOab = String(state.oab_uf || "").toUpperCase();
    if (!numeroOab || !ufOab) throw new Error("OAB não configurada");

    const PAGE_SIZE = 50;
    const MAX_PAGES = 20;
    const items = [];
    let pagesFetched = 0;
    for (let page = 1; page <= MAX_PAGES; page++) {
      const qs = new URLSearchParams({
        numeroOab, ufOab,
        dataDisponibilizacaoInicio: start,
        dataDisponibilizacaoFim: end,
        pagina: String(page),
        itensPorPagina: String(PAGE_SIZE),
      }).toString();
      const url = `https://comunicaapi.pje.jus.br/api/v1/comunicacao?${qs}`;
      // pode falhar por CORS se o CNJ ainda não expuser Access-Control-Allow-Origin
      const resp = await fetch(url, { method: "GET", headers: { "Accept": "application/json" }, mode: "cors" });
      if (!resp.ok) throw new Error(`CNJ HTTP ${resp.status}`);
      const data = await resp.json();
      const pageItems = data.items || [];
      items.push(...pageItems);
      pagesFetched++;
      if (pageItems.length < PAGE_SIZE) break;
    }
    return { start, end, items, pagesFetched };
  };

  const consult = async (mode, extra = {}) => {
    if (!state?.oab_configured) return;
    setConsulting(true); setStage(0);
    const stages = [
      "Consultando API oficial do DJEN...",
      "Buscando publicações vinculadas à sua OAB...",
      "Conferindo resultados...",
      "Analisando publicações...",
      "Organizando resultados...",
    ];
    const tick = setInterval(() => setStage(s => Math.min(s + 1, stages.length - 1)), 900);

    // 1) tenta pelo backend
    let handled = false;
    try {
      const r = await api.post("/djen/consult", { mode, ...extra });
      if (r.data.status === "GEO_BLOCKED") {
        // 2) fallback pelo navegador (que está no BR)
        try {
          const { start, end, items, pagesFetched } = await browserFetchDjen(mode, extra);
          const ing = await api.post("/djen/ingest", {
            mode, range_start: start, range_end: end,
            items, pages_fetched: pagesFetched,
          });
          setLastResult({ ...ing.data, error: false, via: "browser-relay" });
          toast.success(
            (ing.data.new_count > 0
              ? `${ing.data.new_count} nova(s) publicação(ões)`
              : (ing.data.total_returned === 0
                  ? "Nenhuma publicação no período"
                  : "Publicações já conhecidas"))
            + " (consulta feita pelo seu navegador)"
          );
          await loadPublications({ start: ing.data.range_start, end: ing.data.range_end });
          handled = true;
        } catch (be) {
          const msg =
            "🚫 CNJ bloqueou o servidor por IP e o navegador também não conseguiu " +
            "buscar diretamente" +
            (be?.message ? ` (${be.message})` : "") +
            ". Se o erro for CORS, tente novamente em alguns minutos ou peça ao " +
            "administrador para deployar o backend em servidor com IP brasileiro.";
          toast.error(msg, { duration: 14000 });
          setLastResult({ error: true, blocked: true, message: msg });
          handled = true;
        }
      } else if (r.data.status === "ERROR") {
        toast.error(r.data.message || "Falha na consulta DJEN", { duration: 12000 });
        setLastResult({ error: true, blocked: false, message: r.data.message });
        handled = true;
      } else {
        setLastResult({ ...r.data, error: false });
        toast.success(r.data.new_count > 0
          ? `${r.data.new_count} nova(s) publicação(ões)`
          : (r.data.total_returned === 0 ? "Nenhuma publicação localizada" : "Publicações já conhecidas"));
        await loadPublications({ start: r.data.range_start, end: r.data.range_end });
        handled = true;
      }
    } catch (e) {
      if (!handled) toast.error(e?.response?.data?.detail || "Falha na consulta DJEN");
    } finally {
      clearInterval(tick);
      setStage(stages.length);
      await loadStatus();
      setConsulting(false); setStage(0);
    }
  };

  const markRead = async (id) => {
    try {
      await api.post(`/djen/publications/${id}/mark-read`);
      setResults(r => ({ ...r, rows: r.rows.map(x => x.id === id ? { ...x, read: true } : x) }));
      await loadStatus();
    } catch {/* silent */}
  };

  const markAllRead = async () => {
    await api.post("/djen/publications/mark-all-read").catch(()=>null);
    toast.success("Publicações marcadas como lidas");
    loadPublications();
    loadStatus();
  };

  if (loading || !state) return <div className="p-10 text-slate-500 text-sm">Carregando DJEN...</div>;
  if (!state.oab_configured) {
    return <IncompleteOAB nome={state.nome} onFix={() => window.location.href = "/perfil"} />;
  }

  const lastConsult = state.last_consultation;
  const lastAt = lastConsult?.finished_at || lastConsult?.started_at;

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="djen-shell">
      <TopBar/>
      <div className="p-6 lg:p-8 w-full space-y-6 font-sans" data-testid="djen-page">
      <LivePublicationPop pop={livePop} onDismiss={() => setLivePop(null)}/>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold" style={{ color: "#C9953D" }}>HERMES · Central diária de publicações</div>
          <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] text-[#132238] flex items-center gap-3 mt-2">
            <Radio className="w-8 h-8 text-rose-600"/> DJEN
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            API oficial do <strong>Conselho Nacional de Justiça</strong>. Todas as publicações vinculadas
            à sua inscrição <span className="font-mono font-semibold">OAB/{state.oab_uf} {state.oab_numero}</span>,
            em um clique.
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Advogado(a): <strong>{state.nome}</strong>
          </p>
          {(state.health && state.health !== "CONNECTED") || state.mock_mode ? (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="djen-connectivity-badges">
              {state.mock_mode ? (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide">
                  MODO DEMO
                </span>
              ) : state.health === "DEGRADED" ? (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide" title={state.health_message}>
                  ● CNJ temporariamente indisponível
                </span>
              ) : state.health === "UNAVAILABLE" ? (
                <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-semibold uppercase tracking-wide" title={state.health_message}>
                  ● CNJ temporariamente indisponível
                </span>
              ) : state.health === "UNKNOWN" ? (
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold uppercase tracking-wide">
                  ● Aguardando primeira sincronização
                </span>
              ) : null}
            </div>
          ) : state.health === "CONNECTED" ? (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="djen-connectivity-badges">
              <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold uppercase tracking-wide">
                ● CNJ conectado
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Big red button + last-check hint */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
        <button
          onClick={() => consult("today")}
          disabled={consulting}
          className="group relative h-24 rounded-2xl bg-gradient-to-br from-rose-600 to-red-700 hover:from-rose-500 hover:to-red-600 shadow-xl shadow-rose-500/20 text-white font-bold text-2xl uppercase tracking-wider flex items-center justify-center gap-4 transition-all active:scale-[0.99]"
          data-testid="djen-consult-btn"
        >
          {consulting ? (
            <><Loader2 className="w-6 h-6 animate-spin"/> Consultando...</>
          ) : (
            <>
              <Radio className="w-6 h-6"/>
              DJEN
              {state.unread_count > 0 && (
                <span className="absolute top-2 right-3 min-w-[28px] h-7 px-2 rounded-full bg-white text-rose-700 flex items-center justify-center text-sm font-bold" data-testid="djen-unread-badge">
                  {state.unread_count}
                </span>
              )}
            </>
          )}
        </button>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 min-w-[240px] shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Última consulta</div>
          <div className="text-sm text-slate-900 font-medium mt-1">
            {lastAt ? fmtDateTime(lastAt) : "Ainda não consultado hoje"}
          </div>
          {lastConsult?.new_count > 0 && (
            <div className="text-xs text-emerald-700 mt-1">+{lastConsult.new_count} novas</div>
          )}
          <button onClick={() => setShowPeriod(true)} data-testid="djen-period-btn"
            className="mt-3 w-full h-9 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm text-slate-700 inline-flex items-center justify-center gap-2">
            <Calendar className="w-4 h-4"/> Consultar por período
          </button>
          <a href="/prazos/kanban" data-testid="djen-kanban-link"
            className="mt-2 w-full h-9 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 text-sm text-rose-800 inline-flex items-center justify-center gap-2">
            📋 Prazos Hermes
          </a>
        </div>
      </div>

      {/* Progress card while consulting */}
      {consulting && (
        <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/50 p-6 shadow-sm" data-testid="djen-progress">
          <div className="flex items-start gap-3">
            <Loader2 className="w-5 h-5 text-rose-600 animate-spin shrink-0"/>
            <div className="flex-1">
              <div className="font-heading text-lg font-semibold text-slate-900">Consultando DJEN</div>
              <div className="text-xs text-slate-500 mt-0.5">
                Data: {fmtDate(todayIso())} · OAB/{state.oab_uf} {state.oab_numero}
              </div>
              <div className="mt-4">
                <ProgressStages
                  active={stage}
                  stages={[
                    "Consultando API oficial do DJEN...",
                    "Buscando publicações vinculadas à sua OAB...",
                    "Conferindo resultados...",
                    "Analisando publicações...",
                    "Organizando resultados...",
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Last result banner */}
      {!consulting && lastResult && !lastResult.error && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex items-center justify-between" data-testid="djen-last-result">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">
              DJEN — {lastResult.range_start === lastResult.range_end
                ? `Publicações de ${fmtDate(lastResult.range_start)}`
                : `Consulta por período · ${fmtDate(lastResult.range_start)} a ${fmtDate(lastResult.range_end)}`}
            </div>
            <div className="mt-1 flex gap-4 text-sm">
              <span><strong>{lastResult.total_returned}</strong> publicação(ões) retornada(s)</span>
              {lastResult.new_count > 0 && (
                <span className="text-rose-700">🔴 <strong>{lastResult.new_count}</strong> nova(s)</span>
              )}
              <span className="text-slate-500">Páginas: {lastResult.pages_fetched}</span>
            </div>
          </div>
        </div>
      )}
      {!consulting && lastResult?.error && (
        <div className={`rounded-2xl border p-4 flex items-start gap-3 ${
              lastResult.blocked
                ? "border-amber-300 bg-amber-50"
                : "border-rose-200 bg-rose-50"
            }`} data-testid="djen-error">
          <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${
              lastResult.blocked ? "text-amber-700" : "text-rose-700"}`}/>
          <div className="flex-1">
            <div className={`font-medium ${lastResult.blocked ? "text-amber-900" : "text-rose-900"}`}>
              {lastResult.blocked
                ? "API do CNJ bloqueia o IP deste servidor (403)"
                : "Não foi possível concluir a consulta ao DJEN"}
            </div>
            <div className={`text-sm mt-0.5 whitespace-pre-line ${
                lastResult.blocked ? "text-amber-800" : "text-rose-800"}`}>
              {lastResult.message}
            </div>
          </div>
          {!lastResult.blocked && (
            <button onClick={()=>consult("today")}
              className="h-9 px-3 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-medium inline-flex items-center gap-2">
              <RefreshCcw className="w-4 h-4"/> Tentar novamente
            </button>
          )}
        </div>
      )}

      {/* Publications */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Publicações salvas</div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              {results.total} publicação(ões) no histórico
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {state.unread_count > 0 && (
              <button onClick={markAllRead} className="text-xs text-slate-500 hover:text-slate-900 underline"
                data-testid="djen-mark-all-read-btn">
                Marcar todas como lidas
              </button>
            )}
          </div>
        </div>
        {results.rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="djen-empty">
            Nenhuma publicação. Clique no botão DJEN vermelho acima para consultar o dia atual.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {results.rows.map(pub => (
              <PublicationCard
                key={pub.id}
                pub={pub}
                onOpen={()=>{ setReader(pub); markRead(pub.id); }}
                onSend={(channel)=>setSender({ pub, channel })}
                onSave={()=>window.open(`${API}/djen/publications/${pub.id}/pdf`, '_blank')}
                onPrint={()=>{ setReader(pub); markRead(pub.id); setTimeout(()=>window.print(), 500); }}
                onAnalyze={()=>analyze(pub)}
                onFulfill={()=>setFulfill(pub)}
                analyzing={analyzingId === pub.id}
              />
            ))}
          </div>
        )}
      </div>

      <div className="text-[11px] text-slate-500 text-center">
        Esta funcionalidade consulta publicações disponíveis no DJEN.
        Comunicações submetidas a regime próprio de intimação devem ser verificadas conforme
        as regras processuais aplicáveis. Fonte oficial: DJEN/CNJ.
      </div>

      {showPeriod && (
        <PeriodModal
          initial={customRange}
          onCancel={()=>setShowPeriod(false)}
          onGo={(payload) => { setShowPeriod(false); consult(payload.mode, payload.extra); }}
        />
      )}
      {reader && <ReadModal pub={reader} onClose={()=>setReader(null)} onSend={(channel)=>{ setReader(null); setSender({ pub: reader, channel }); }}/>}
      {sender && <SendModal ctx={sender} onClose={()=>setSender(null)}/>}
      {fulfill && <FulfillDrawer pub={fulfill} onClose={()=>setFulfill(null)}
        onCreated={(preparedId) => {
          setResults(prev => ({ ...prev, rows: prev.rows.map(x =>
            x.id === fulfill.id ? { ...x, prepared_document_id: preparedId } : x
          )}));
          setFulfill(null);
        }}/>}
      {PaywallElement}
      </div>
    </div>
  );
}

function PublicationCard({ pub, onOpen, onSend, onSave, onPrint, onAnalyze, onFulfill, analyzing }) {
  const partes = (pub.partes || []).map(p => p.nome || p).filter(Boolean).slice(0, 4).join(" × ") || "—";
  const displayText = pickDisplayText(pub);
  const preview = displayText.replace(/\s+/g, " ").slice(0, 260);
  const a = pub.ai_analysis;
  const urgTone = a?.urgencia === "alta" ? "border-rose-300 bg-rose-50 text-rose-800"
                : a?.urgencia === "media" ? "border-amber-300 bg-amber-50 text-amber-800"
                : "border-slate-200 bg-slate-50 text-slate-800";
  const hasPrepared = !!pub.prepared_document_id;
  return (
    <div className={`px-6 py-4 ${!pub.read ? "bg-[#faf7f0]/60 border-l-2 border-l-[#c9953d]" : ""}`} data-testid={`djen-pub-${pub.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {!pub.read && <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold bg-[#c9953d] text-white uppercase tracking-wider">Nova</span>}
            <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-800 uppercase tracking-wider border border-slate-200">{sanitizeDjenText(pub.tipo || "publicação")}</span>
            <CourtLabel raw={pub.tribunal} size="xs" variant="chip" />
            <span className="text-[11px] text-slate-500">·</span>
            <span className="text-[11px] font-medium text-slate-700">{fmtDate(pub.data_disponibilizacao)}</span>
            {a && <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-800 border border-indigo-200">IA · {a.tipo}</span>}
            {pub.linked_client && <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">↳ {pub.linked_client.nome}</span>}
            {hasPrepared && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-900 text-white"><ClipboardList className="w-3 h-3"/> Preparado</span>}
          </div>
          <div className="mt-1.5 font-mono text-[13px] font-semibold text-slate-900">{pub.numero_processo || "—"}</div>
          <div className="text-[11px] text-slate-700 mt-0.5">{sanitizeDjenText(pub.orgao || "")}</div>
          {partes !== "—" && <div className="text-[12px] text-slate-800 mt-1.5"><span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Partes: </span>{sanitizeDjenText(partes)}</div>}
          <p className="text-[13px] text-slate-800 mt-2 line-clamp-2 leading-relaxed">{preview}{displayText.length > 260 ? "..." : ""}</p>
          {a && !a.parse_error && (
            <div className={`mt-3 rounded-lg border p-3 ${urgTone}`} data-testid={`djen-analysis-${pub.id}`}>
              <div className="text-[10px] uppercase tracking-widest font-semibold">Análise Hermes IA</div>
              <div className="text-sm mt-1"><strong>Resumo:</strong> {sanitizeDjenText(a.resumo)}</div>
              {a.providencia && <div className="text-sm mt-1"><strong>Providência:</strong> {sanitizeDjenText(a.providencia)}</div>}
              <div className="text-sm mt-1 flex items-center gap-2 flex-wrap">
                <strong>Prazo:</strong>
                {a.tem_prazo === "sim" && a.prazo_dias
                  ? <span>{a.prazo_dias} dias{a.prazo_termo_inicial && ` a contar de ${fmtDate(a.prazo_termo_inicial)}`}</span>
                  : a.tem_prazo === "nao"
                  ? <span>Não identificado</span>
                  : <span className="inline-flex items-center gap-1 text-amber-700 bg-white border border-amber-200 rounded-full px-2 py-0.5 text-[10px] font-semibold">⚠ PRAZO NÃO IDENTIFICADO COM SEGURANÇA</span>
                }
              </div>
              {a.prazo_alerta && <div className="text-[11px] mt-1 italic">{sanitizeDjenText(a.prazo_alerta)}</div>}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <ActionBtn icon={FileText} label="Ler publicação" onClick={onOpen} testid={`djen-read-${pub.id}`} primary/>
        <ActionBtn icon={ClipboardList} label={hasPrepared ? "Abrir preparado" : "Cumprir prazo"}
          onClick={onFulfill} testid={`djen-fulfill-${pub.id}`}
          variant={hasPrepared ? "success" : "gold"}/>
        {!a && (
          <ActionBtn icon={analyzing ? Loader2 : Radio} label={analyzing ? "Analisando..." : "Analisar com IA"}
            onClick={onAnalyze} testid={`djen-analyze-${pub.id}`}
            className={analyzing ? "animate-spin" : ""}/>
        )}
        <MoreMenu>
          <ActionBtn icon={Save} label="Salvar PDF" onClick={onSave} testid={`djen-save-${pub.id}`} inMenu/>
          <ActionBtn icon={Printer} label="Imprimir" onClick={onPrint} testid={`djen-print-${pub.id}`} inMenu/>
          <ActionBtn icon={MessageCircle} label="Enviar por WhatsApp" onClick={()=>onSend("whatsapp")} testid={`djen-wa-${pub.id}`} inMenu/>
          <ActionBtn icon={Mail} label="Enviar por e-mail" onClick={()=>onSend("email")} testid={`djen-mail-${pub.id}`} inMenu/>
        </MoreMenu>
      </div>
      <div className="mt-2 text-[10px] text-slate-500 uppercase tracking-wider">Fonte oficial: DJEN/CNJ</div>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, testid, primary, variant, inMenu, className = "" }) {
  if (inMenu) {
    return (
      <button onClick={onClick} data-testid={testid}
        className="w-full text-left px-3 py-2 rounded-md text-[13px] font-medium text-slate-800 hover:bg-slate-100 inline-flex items-center gap-2">
        <Icon className={`w-4 h-4 text-slate-500 ${className}`}/> {label}
      </button>
    );
  }
  let base = "h-8 px-3 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors";
  if (primary) base += " bg-slate-900 hover:bg-slate-800 text-white";
  else if (variant === "gold") base += " bg-[#c9953d] hover:bg-[#b57f2b] text-white shadow-sm";
  else if (variant === "success") base += " bg-emerald-700 hover:bg-emerald-800 text-white";
  else base += " border border-slate-300 hover:bg-slate-50 text-slate-800";
  return (
    <button onClick={onClick} data-testid={testid} className={`${base}`}>
      <Icon className={`w-4 h-4 ${className}`}/> {label}
    </button>
  );
}

function MoreMenu({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(v => !v)} data-testid="djen-more-menu"
        className="h-8 w-8 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 inline-flex items-center justify-center">
        <MoreHorizontal className="w-4 h-4"/>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className="absolute right-0 mt-1 w-52 rounded-lg border border-slate-200 bg-white shadow-lg z-50 py-1"
            onClick={() => setOpen(false)}>
            {children}
          </div>
        </>
      )}
    </div>
  );
}

function ReadModal({ pub, onClose, onSend }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[85vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={e=>e.stopPropagation()} data-testid="djen-read-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold flex items-center gap-2">
              {sanitizeDjenText(pub.tipo || "Publicação")}
              <span className="text-slate-400">·</span>
              <CourtLabel raw={pub.tribunal} size="xs" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-slate-900 font-mono mt-1">{pub.numero_processo || "—"}</h3>
            <div className="text-[13px] text-slate-800 mt-0.5">{sanitizeDjenText(pub.orgao || "")}</div>
            <div className="text-xs text-slate-600 mt-0.5">Disponibilizado em {fmtDate(pub.data_disponibilizacao)} · Publicado em {fmtDate(pub.data_publicacao)}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {(pub.partes || []).length > 0 && (
            <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold mb-1">Partes</div>
              <ul className="text-[13px] text-slate-900 space-y-0.5">
                {(pub.partes || []).map((p, i) => (
                  <li key={i}>{sanitizeDjenText(p.nome || p)} {p.polo && <span className="text-[10px] text-slate-600 uppercase">· {p.polo}</span>}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold mb-1">Texto oficial (fonte DJEN/CNJ)</div>
          <div className="rounded-lg bg-white border border-slate-200 p-4 text-[13.5px] text-slate-900 whitespace-pre-wrap leading-relaxed">
            {pickDisplayText(pub) || "(sem conteúdo textual)"}
          </div>
          <div className="mt-3 text-[10px] text-slate-500 uppercase tracking-wider">Fonte oficial: DJEN/CNJ · Consulta feita pelo Hermes · Texto não é alterado pelo Hermes.</div>
        </div>
        <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
          <button onClick={()=>window.print()} className="h-9 px-3 rounded-lg border border-slate-300 text-sm text-slate-800 inline-flex items-center gap-2 hover:bg-white"><Printer className="w-4 h-4"/> Imprimir</button>
          <button onClick={()=>onSend("whatsapp")} className="h-9 px-3 rounded-lg border border-slate-300 text-sm text-slate-800 inline-flex items-center gap-2 hover:bg-white"><MessageCircle className="w-4 h-4"/> WhatsApp</button>
          <button onClick={()=>onSend("email")} className="h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm inline-flex items-center gap-2"><Mail className="w-4 h-4"/> E-mail</button>
        </div>
      </div>
    </div>
  );
}


/* -----------------------------------------------------------------
 *   CUMPRIR PRAZO COM HERMES · Drawer (P1)
 *   - Analisa a publicação antes da geração
 *   - Nunca inventa prazo
 *   - Gera preparado revisável em /preparados (status AGUARDANDO REVISÃO)
 *   - Idempotente por publication_id + action_type
 *   - Nunca protocola automaticamente
 * ----------------------------------------------------------------- */
function FulfillDrawer({ pub, onClose, onCreated }) {
  const [phase, setPhase] = useState("analyzing");  // analyzing | ready | preparing | done | error
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);
  const [prepared, setPrepared] = useState(pub.prepared_document_id || null);
  const { authorize, PaywallElement } = usePaywall("PECA_GERACAO");

  useEffect(() => {
    let stop = false;
    const run = async () => {
      if (pub.prepared_document_id) {
        setPhase("done");
        setPrepared(pub.prepared_document_id);
        return;
      }
      try {
        const r = await api.post(`/djen/publications/${pub.id}/plan-fulfillment`);
        if (!stop) { setPlan(r.data); setPhase("ready"); }
      } catch (e) {
        if (!stop) { setError(e?.response?.data?.detail || "Não foi possível analisar esta publicação agora."); setPhase("error"); }
      }
    };
    run();
    return () => { stop = true; };
  }, [pub.id, pub.prepared_document_id]);

  const doPrepare = async () => {
    // Paywall gate — geração de peça (R$5 always_paid)
    const authorization = await authorize({ quantity: 1 });
    if (!authorization) return;
    setPhase("preparing");
    try {
      const r = await api.post(`/djen/publications/${pub.id}/prepare-fulfillment`, {
        plan_signature: plan?.signature || "",
      });
      await authorization.commit();
      setPrepared(r.data.prepared_document_id);
      setPhase("done");
      onCreated?.(r.data.prepared_document_id);
    } catch (e) {
      await authorization.release();
      const msg = e?.response?.data?.detail;
      // 409 idempotência: já existe
      if (e?.response?.status === 409 && e?.response?.data?.prepared_document_id) {
        setPrepared(e.response.data.prepared_document_id);
        setPhase("done");
        return;
      }
      setError(msg || "Não foi possível concluir o documento.");
      setPhase("error");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" onClick={onClose} data-testid="djen-fulfill-drawer">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm"/>
      <div className="relative w-full max-w-xl bg-white shadow-2xl overflow-y-auto flex flex-col" onClick={e=>e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#c9953d] font-bold">Cumprir Prazo com Hermes</div>
            <div className="mt-1 font-mono text-sm text-slate-900">{pub.numero_processo || "—"}</div>
            <CourtLabel raw={pub.tribunal} size="xs" className="mt-1"/>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5"/></button>
        </div>

        <div className="flex-1 px-6 py-5">
          {phase === "analyzing" && (
            <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid="fulfill-analyzing">
              <Loader2 className="w-8 h-8 text-[#c9953d] animate-spin"/>
              <div className="text-sm text-slate-800 font-semibold">Analisando publicação...</div>
              <div className="text-xs text-slate-500">Identificando processo, tribunal, determinação e prazo</div>
            </div>
          )}

          {phase === "ready" && plan && (
            <div className="space-y-4" data-testid="fulfill-ready">
              <FulfillRow label="Processo" value={plan.process_number || pub.numero_processo}/>
              <FulfillRow label="Tribunal" value={<CourtLabel raw={plan.court || pub.tribunal} />} />
              {plan.court_unit && <FulfillRow label="Unidade" value={sanitizeDjenText(plan.court_unit)}/>}
              <FulfillRow label="Natureza" value={sanitizeDjenText(plan.publication_type || "—")}/>
              <FulfillRow label="Determinação identificada" value={sanitizeDjenText(plan.determination || "—")} block/>
              {plan.status === "no_action_needed" ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800" data-testid="fulfill-no-action">
                  Não foi identificada uma providência a ser cumprida nesta publicação.
                </div>
              ) : (
                <>
                  <FulfillRow label="Providência sugerida" value={sanitizeDjenText(plan.providence || "—")} block/>
                  <FulfillRow label="Documento a preparar" value={sanitizeDjenText(plan.document_type || "Manifestação")} />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold mb-1">Prazo identificado</div>
                    {plan.deadline_confidence === "high" ? (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-center gap-2" data-testid="fulfill-deadline-high">
                        <CheckCircle2 className="w-4 h-4"/>
                        <span><strong>{plan.deadline_days} {plan.deadline_unit || "dias"}</strong>{plan.deadline_starts_on && ` a contar de ${fmtDate(plan.deadline_starts_on)}`}</span>
                      </div>
                    ) : plan.deadline_confidence === "medium" ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="fulfill-deadline-medium">
                        <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-4 h-4"/> Possível prazo identificado</div>
                        <div className="mt-1 text-[13px]">{plan.deadline_days} dias — revise antes de continuar.</div>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="fulfill-deadline-unknown">
                        <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-4 h-4"/> Prazo não identificado com segurança</div>
                        <div className="mt-1 text-[12.5px]">Revise a publicação antes de continuar. O Hermes não inventará um prazo.</div>
                      </div>
                    )}
                  </div>
                  {plan.ambiguity_note && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="fulfill-ambiguity">
                      <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="w-4 h-4"/> Revisão necessária</div>
                      <div className="mt-1 text-[12.5px]">{sanitizeDjenText(plan.ambiguity_note)}</div>
                    </div>
                  )}
                  {plan.confidence && (
                    <div className="text-[11px] text-slate-500 italic">Confiança da análise: {plan.confidence}</div>
                  )}
                </>
              )}
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-700 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-slate-500 mt-0.5 shrink-0"/>
                <span>O Hermes prepara o documento para sua revisão. Nada é protocolado automaticamente. Você mantém a decisão profissional.</span>
              </div>
            </div>
          )}

          {phase === "preparing" && (
            <div className="flex flex-col items-center gap-3 py-16 text-center" data-testid="fulfill-preparing">
              <Loader2 className="w-8 h-8 text-[#c9953d] animate-spin"/>
              <div className="text-sm text-slate-800 font-semibold">Preparando documento...</div>
              <div className="text-xs text-slate-500">O material ficará disponível em Preparados para sua revisão</div>
            </div>
          )}

          {phase === "done" && (
            <div className="flex flex-col items-center gap-3 py-12 text-center" data-testid="fulfill-done">
              <div className="w-14 h-14 rounded-full bg-emerald-100 inline-flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-700"/>
              </div>
              <div className="text-lg font-heading text-slate-900">Preparado criado</div>
              <div className="text-sm text-slate-600 max-w-sm">Enviamos o documento para <strong>Preparados</strong> com status <strong>Aguardando revisão</strong>. Nada foi protocolado.</div>
              <a href={`/preparado/${prepared}`} target="_blank" rel="noopener noreferrer"
                data-testid="fulfill-open-prepared"
                className="mt-2 h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm inline-flex items-center gap-2">
                <ExternalLink className="w-4 h-4"/> Abrir preparado
              </a>
            </div>
          )}

          {phase === "error" && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="fulfill-error">
              <div className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4"/> Falha na análise</div>
              <div className="mt-1 text-[13px]">{error}</div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 sticky bottom-0">
          {phase === "ready" && plan?.status !== "no_action_needed" && (
            <button onClick={doPrepare} data-testid="fulfill-prepare-btn"
              className="h-10 px-4 rounded-lg bg-[#c9953d] hover:bg-[#b57f2b] text-white text-sm font-semibold inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4"/> Preparar cumprimento
            </button>
          )}
          <button onClick={onClose} data-testid="fulfill-cancel-btn"
            className="h-10 px-4 rounded-lg border border-slate-300 text-sm text-slate-800 hover:bg-white">
            {phase === "done" ? "Fechar" : "Cancelar"}
          </button>
        </div>
      </div>
      {PaywallElement}
    </div>
  );
}

function FulfillRow({ label, value, block }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold mb-0.5">{label}</div>
      <div className={block ? "text-[13px] text-slate-900 leading-relaxed" : "text-[13px] text-slate-900"}>{value || "—"}</div>
    </div>
  );
}

function PeriodModal({ initial, onCancel, onGo }) {
  const [range, setRange] = useState(initial);
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-6" onClick={e=>e.stopPropagation()} data-testid="djen-period-modal">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">Consulta por período</div>
        <h3 className="font-heading text-xl font-semibold text-slate-900">Escolha o intervalo</h3>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {PERIODS.map(p => (
            <button key={p.key} onClick={()=>onGo({mode:p.key})}
              className="h-11 rounded-lg border border-slate-200 hover:bg-slate-50 hover:border-[#d4a24c] text-sm text-slate-800 font-medium"
              data-testid={`djen-period-${p.key}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-6 border-t border-slate-100 pt-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Período personalizado</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <div className="text-xs text-slate-600 mb-1">Data inicial</div>
              <input type="date" value={range.start} onChange={e=>setRange({...range, start:e.target.value})}
                className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm" data-testid="djen-period-start"/>
            </label>
            <label className="block">
              <div className="text-xs text-slate-600 mb-1">Data final</div>
              <input type="date" value={range.end} onChange={e=>setRange({...range, end:e.target.value})}
                className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm" data-testid="djen-period-end"/>
            </label>
          </div>
          <button onClick={()=>onGo({mode:"period", extra:{start:range.start, end:range.end}})}
            className="mt-3 w-full h-10 rounded-lg bg-[#d4a24c] hover:bg-[#c48a3e] text-black font-semibold text-sm"
            data-testid="djen-period-search-btn">
            Pesquisar DJEN
          </button>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-900">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function SendModal({ ctx, onClose }) {
  const { pub, channel } = ctx;
  const _clean = ((pub.texto_display && pub.texto_display.trim()) || pub.texto || "");
  const [message, setMessage] = useState(
    channel === "whatsapp"
      ? `Olá. Identificamos uma nova publicação no processo nº ${pub.numero_processo || "(processo)"}. Segue trecho para sua ciência:\n\n"${_clean.slice(0, 300)}${_clean.length > 300 ? "..." : ""}"\n\nFonte: DJEN/CNJ.`
      : `Prezado(a),\n\nSegue nova publicação do DJEN no processo nº ${pub.numero_processo || "(processo)"} — ${fmtDate(pub.data_disponibilizacao)}.\n\n${_clean.slice(0, 800)}${_clean.length > 800 ? "..." : ""}\n\nFonte oficial: DJEN/CNJ.`
  );
  const [dest, setDest] = useState("");
  const [subject, setSubject] = useState(`Nova publicação DJEN — Processo nº ${pub.numero_processo || ""}`);

  const send = () => {
    if (!dest) return toast.error(channel === "whatsapp" ? "Informe o número" : "Informe o e-mail");
    if (channel === "whatsapp") {
      const clean = dest.replace(/\D/g, "");
      const url = `https://wa.me/${clean.startsWith("55") ? clean : "55" + clean}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    } else {
      const url = `mailto:${dest}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
      window.location.href = url;
    }
    toast.success("Abrindo " + (channel === "whatsapp" ? "WhatsApp" : "e-mail") + "...");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl" onClick={e=>e.stopPropagation()} data-testid="djen-send-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Enviar publicação</div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">{channel === "whatsapp" ? "WhatsApp" : "E-mail"}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-6 space-y-3">
          {channel === "whatsapp" ? (
            <label className="block">
              <div className="text-xs text-slate-600 mb-1">Número (com DDD, ex.: 31999998888)</div>
              <input value={dest} onChange={e=>setDest(e.target.value)}
                className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm"
                placeholder="31999998888" data-testid="djen-send-dest"/>
            </label>
          ) : (
            <>
              <label className="block">
                <div className="text-xs text-slate-600 mb-1">E-mail do destinatário</div>
                <input value={dest} onChange={e=>setDest(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm"
                  placeholder="cliente@exemplo.com" data-testid="djen-send-dest"/>
              </label>
              <label className="block">
                <div className="text-xs text-slate-600 mb-1">Assunto</div>
                <input value={subject} onChange={e=>setSubject(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm"
                  data-testid="djen-send-subject"/>
              </label>
            </>
          )}
          <label className="block">
            <div className="text-xs text-slate-600 mb-1">Mensagem (edite antes de enviar)</div>
            <textarea value={message} onChange={e=>setMessage(e.target.value)} rows={8}
              className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm resize-y"
              data-testid="djen-send-message"/>
          </label>
          <div className="text-[11px] text-slate-500">
            <Info className="inline w-3 h-3 mr-1"/>
            Nenhum envio é feito automaticamente — você confirma a mensagem no {channel === "whatsapp" ? "WhatsApp" : "e-mail"} antes de disparar.
          </div>
        </div>
        <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700">Cancelar</button>
          <button onClick={send}
            className="h-10 px-5 rounded-lg bg-[#d4a24c] hover:bg-[#c48a3e] text-black text-sm font-semibold inline-flex items-center gap-2"
            data-testid="djen-send-confirm">
            <Send className="w-4 h-4"/> Confirmar envio
          </button>
        </div>
      </div>
    </div>
  );
}


function LivePublicationPop({ pop, onDismiss }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!pop) { setVisible(false); return; }
    // fade in
    const inT = setTimeout(() => setVisible(true), 30);
    // auto-dismiss após 15s
    const outT = setTimeout(() => {
      setVisible(false);
      setTimeout(onDismiss, 300);
    }, 15000);
    return () => { clearTimeout(inT); clearTimeout(outT); };
  }, [pop, onDismiss]);

  if (!pop) return null;
  const { count, publications } = pop;
  return (
    <div
      className={
        "fixed bottom-6 right-6 z-50 w-[360px] rounded-2xl border border-rose-300 " +
        "bg-white shadow-2xl shadow-rose-500/20 transition-all duration-300 " +
        (visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none")
      }
      data-testid="djen-live-pop"
      role="status"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-700 grid place-items-center animate-pulse">
            <Radio className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-rose-600 font-semibold">
              Nova publicação do DJEN
            </div>
            <div className="text-slate-900 font-semibold mt-0.5" data-testid="djen-live-pop-count">
              {count} nova{count > 1 ? "s" : ""} publica{count > 1 ? "ções" : "ção"}
            </div>
            {publications?.slice(0, 3).map((p, i) => (
              <div key={p.id || i} className="mt-2 text-xs text-slate-600 truncate">
                • <strong>{p.numero_processo || "—"}</strong>
                <span className="text-slate-400"> — {p.tribunal || "?"} · {p.tipo || "?"}</span>
              </div>
            ))}
            {count > 3 && (
              <div className="mt-1 text-xs text-slate-500">e mais {count - 3}…</div>
            )}
          </div>
          <button
            onClick={onDismiss}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Fechar"
            data-testid="djen-live-pop-close"
          >
            <X className="w-4 h-4"/>
          </button>
        </div>
      </div>
      <div className="h-1 bg-gradient-to-r from-rose-500 to-red-600 rounded-b-2xl overflow-hidden">
        <div
          className="h-full bg-white/40"
          style={{ animation: "djenPopBar 15s linear forwards" }}
        />
      </div>
      <style>{`
        @keyframes djenPopBar {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

